import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Add, Trash } from "iconsax-react";
import { FileUploader } from "@/shared/components";
import { Button, ButtonGroup, Checkbox, Toast } from "@/shared/ui";
import type { DocumentTab, ImportWorkerFormValues } from "../types";

interface DocumentField {
  name: keyof ImportWorkerFormValues;
  labelKey: string;
  accept: string;
  required?: boolean;
}

const CONTRACT_DOC_FIELDS: DocumentField[] = [
  { name: "contract_file", labelKey: "documents.contractFile", accept: ".pdf" },
  { name: "job_application_file", labelKey: "documents.jobApplication", accept: ".pdf" },
  { name: "order_on_hiring_file", labelKey: "documents.orderOnHiring", accept: ".pdf" },
];

const PERSONAL_DOC_FIELDS: DocumentField[] = [
  { name: "photo_file", labelKey: "documents.photo", accept: ".pdf,.jpg,.jpeg,.png" },
  { name: "national_id_file", labelKey: "documents.nationalId", accept: ".pdf,.jpg,.jpeg,.png" },
  { name: "bank_certificate_file", labelKey: "documents.bankCertificate", accept: ".pdf,.jpg,.jpeg,.png" },
  { name: "personal_sheet_file", labelKey: "documents.personalSheet", accept: ".pdf,.jpg,.jpeg,.png" },
];

type MultiFileField = "diploma_file" | "diploma_transcript_file" | "social_category_document_file";

interface Props {
  activeTab: DocumentTab;
  onTabChange: (tab: DocumentTab) => void;
  draftFilenames?: Record<string, string>;
}

export default function DocumentsSection({ activeTab, onTabChange, draftFilenames }: Props) {
  const { t } = useTranslation("ImportWorkerPage");

  const {
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<ImportWorkerFormValues>();

  const gender = watch("gender");
  const isStudent = watch("is_student");
  const noEducation = watch("has_no_education");
  const noExperience = watch("has_no_work_experience");
  const diplomaFiles = watch("diploma_file") || [];
  const transcriptFiles = watch("diploma_transcript_file") || [];
  const socialFiles = watch("social_category_document_file") || [];

  function handleUpdateMultiFile(field: MultiFileField, index: number, file: File | null) {
    const current = (watch(field) as (File | null)[]) || [];
    const updated = [...current];
    updated[index] = file;
    setValue(field, updated, { shouldValidate: true });
    if (field === "diploma_transcript_file") {
      trigger("diploma_file");
    }
  }

  function handleAddDiplomaPair() {
    setValue("diploma_file", [...diplomaFiles, null], { shouldValidate: true });
    setValue("diploma_transcript_file", [...transcriptFiles, null], { shouldValidate: true });
  }

  function handleRemoveDiplomaPair(index: number) {
    setValue(
      "diploma_file",
      diplomaFiles.filter((_, i) => i !== index),
      { shouldValidate: true },
    );
    setValue(
      "diploma_transcript_file",
      transcriptFiles.filter((_, i) => i !== index),
      { shouldValidate: true },
    );
  }

  function handleAddSocialFile() {
    setValue("social_category_document_file", [...socialFiles, null], { shouldValidate: true });
  }

  function handleRemoveSocialFile(index: number) {
    setValue(
      "social_category_document_file",
      socialFiles.filter((_, i) => i !== index),
      { shouldValidate: true },
    );
  }

  function renderSingleFileField(field: DocumentField) {
    const value = watch(field.name) as File | null;
    const fieldErrors = errors[field.name];
    const draftName = draftFilenames?.[field.name];

    return (
      <div key={field.name} className="flex flex-col gap-1">
        <FileUploader
          label={t(field.labelKey)}
          hasRequiredLabel={field.required}
          maxSizeMB={50}
          accept={field.accept}
          value={value}
          onChange={(file) => setValue(field.name as keyof ImportWorkerFormValues, file, { shouldValidate: true })}
          error={fieldErrors?.message ? t(fieldErrors.message as string) : undefined}
          namespace="ImportWorkerPage"
        />
        {!value && draftName && (
          <p className="text-body-regular-xs content-action-info">
            {t("documents.draftFileUploaded", { filename: draftName })}
          </p>
        )}
      </div>
    );
  }

  function renderDiplomaPairs() {
    return (
      <div className="flex flex-col gap-4 col-span-2">
        {diplomaFiles.map((_, index) => {
          const diplomaDraftName = draftFilenames?.[`diploma_file[${index}]`];
          const transcriptDraftName = draftFilenames?.[`diploma_transcript_file[${index}]`];
          return (
            <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <div className="flex flex-col gap-1">
                <FileUploader
                  label={diplomaFiles.length > 1 ? `${t("documents.diploma")} №${index + 1}` : t("documents.diploma")}
                  maxSizeMB={50}
                  accept=".pdf,.jpg,.jpeg,.png"
                  value={diplomaFiles[index]}
                  onChange={(f) => handleUpdateMultiFile("diploma_file", index, f)}
                  namespace="ImportWorkerPage"
                />
                {!diplomaFiles[index] && diplomaDraftName && (
                  <p className="text-body-regular-xs content-action-info">
                    {t("documents.draftFileUploaded", { filename: diplomaDraftName })}
                  </p>
                )}
              </div>
              <div className="flex items-start gap-2">
                <div className="flex-1 flex flex-col gap-1">
                  <FileUploader
                    label={diplomaFiles.length > 1 ? `${t("documents.diplomaTranscript")} №${index + 1}` : t("documents.diplomaTranscript")}
                    maxSizeMB={50}
                    accept=".pdf,.jpg,.jpeg,.png"
                    value={transcriptFiles[index] ?? null}
                    onChange={(f) => handleUpdateMultiFile("diploma_transcript_file", index, f)}
                    namespace="ImportWorkerPage"
                  />
                  {!transcriptFiles[index] && transcriptDraftName && (
                    <p className="text-body-regular-xs content-action-info">
                      {t("documents.draftFileUploaded", { filename: transcriptDraftName })}
                    </p>
                  )}
                </div>
                {diplomaFiles.length > 1 && (
                  <Button
                    variant="danger"
                    isIconButton
                    size="sm"
                    type="button"
                    onClick={() => handleRemoveDiplomaPair(index)}
                    className="mt-7 shrink-0">
                    <Trash size={16} color="currentColor" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        {errors.diploma_file && typeof errors.diploma_file.message === "string" && (
          <p className="text-body-regular-xs content-action-negative">{t(errors.diploma_file.message)}</p>
        )}
        <Button type="button" variant="secondary" size="md" onClick={handleAddDiplomaPair} className="w-full">
          <Add size={16} color="currentColor" />
          {t("documents.addFile")}
        </Button>
      </div>
    );
  }

  function renderSocialCategoryFiles() {
    return (
      <div className="flex flex-col gap-2 col-span-2">
        <label className="text-label-sm content-base-primary">{t("documents.socialCategoryDocument")}</label>
        {errors.social_category_document_file && typeof errors.social_category_document_file.message === "string" && (
          <p className="text-body-regular-xs content-action-negative">{t(errors.social_category_document_file.message)}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {socialFiles.map((file, index) => {
            const socialDraftName = draftFilenames?.[`social_category_document_file[${index}]`];
            return (
              <div key={index} className="flex items-start gap-2">
                <div className="flex-1 flex flex-col gap-1">
                  <FileUploader
                    maxSizeMB={50}
                    accept=".pdf,.jpg,.jpeg,.png"
                    value={file}
                    onChange={(f) => handleUpdateMultiFile("social_category_document_file", index, f)}
                    namespace="ImportWorkerPage"
                  />
                  {!file && socialDraftName && (
                    <p className="text-body-regular-xs content-action-info">
                      {t("documents.draftFileUploaded", { filename: socialDraftName })}
                    </p>
                  )}
                </div>
                <Button
                  variant="danger"
                  isIconButton
                  size="sm"
                  type="button"
                  onClick={() => handleRemoveSocialFile(index)}
                  className="mt-3 shrink-0">
                  <Trash size={16} color="currentColor" />
                </Button>
              </div>
            );
          })}
        </div>
        <Button type="button" variant="secondary" size="md" onClick={handleAddSocialFile} className="w-full">
          <Add size={16} color="currentColor" />
          {t("documents.addFile")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-display-2xs content-base-primary">{t("sections.documents")}</h2>

      <div className="flex">
        <ButtonGroup
          options={[
            { label: t("documents.tabs.contract"), value: "contract" },
            { label: t("documents.tabs.personalRecord"), value: "personal" },
          ]}
          value={activeTab}
          onChange={(v) => onTabChange(v as DocumentTab)}
        />
      </div>

      {activeTab === "contract" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {CONTRACT_DOC_FIELDS.map((field) => renderSingleFileField(field))}
        </div>
      )}

      {activeTab === "personal" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PERSONAL_DOC_FIELDS.map((field) => renderSingleFileField(field))}

          {gender === "male" && renderSingleFileField({
            name: "military_certificate_file",
            labelKey: "documents.militaryCertificate",
            accept: ".pdf,.jpg,.jpeg,.png",
          })}

          {isStudent && renderSingleFileField({
            name: "enrollment_verification_file",
            labelKey: "documents.enrollmentVerification",
            accept: ".pdf,.jpg,.jpeg,.png",
          })}

          <div className="col-span-2 flex flex-col gap-3">
            <Checkbox
              state={noExperience ? "checked" : "unchecked"}
              onChange={() => setValue("has_no_work_experience", !noExperience, { shouldValidate: true })}
              label={t("documents.noExperienceLabel")}
            />
            {!noExperience && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderSingleFileField({
                    name: "work_proof_file",
                    labelKey: "documents.workProof",
                    accept: ".pdf,.jpg,.jpeg,.png",
                    required: true,
                  })}
                  {renderSingleFileField({
                    name: "enpf_certificate_file",
                    labelKey: "documents.enpfCertificate",
                    accept: ".pdf,.jpg,.jpeg,.png",
                  })}
                </div>
                <Toast
                  color="grey"
                  text={t("documents.laborBookHint")}
                  closable={false}
                  autoClose={false}
                  isFullWidth
                />
              </>
            )}
          </div>

          <div className="col-span-2 flex flex-col gap-3">
            <Checkbox
              state={noEducation ? "checked" : "unchecked"}
              onChange={() => setValue("has_no_education", !noEducation, { shouldValidate: true })}
              label={t("documents.noEducationLabel")}
            />
            {!noEducation && renderDiplomaPairs()}
          </div>

          {renderSocialCategoryFiles()}
        </div>
      )}
    </div>
  );
}
